/* eslint-disable react/style-prop-object */
/* eslint-disable no-underscore-dangle */
import { useFocusEffect } from '@react-navigation/native';
import { Box, Spinner, Text, useBreakpointValue, useToken, VStack } from 'native-base';
import React, { useMemo, useRef } from 'react';
import { ScrollView } from 'react-native';

import Accordion from 'app/components/Accordion';
import AttractionSupportInfo from 'app/components/AttractionSupportInfo';
import HTMLCollapsible from 'app/components/HTMLCollapsible';
import { MapWithPin, TicketIncluded } from 'app/components/icons';
import ChatBubbleCheck from 'app/components/icons/ChatBubbleCheck';
import PhoneHours from 'app/components/icons/PhoneHours';
import PhoneQR from 'app/components/icons/PhoneQR';
import Star from 'app/components/icons/Star';
import TicketOutlinePlus from 'app/components/icons/TicketOutlinePlus';
import IconTitle from 'app/components/IconTitle';
import ImageCarousel, { ImageCarouselImage } from 'app/components/ImageCarousel';
import LinkTicketsPrompt from 'app/components/LinkTicketsPrompt';
import Loading from 'app/components/Loading';
import Notices from 'app/components/Notices';
import ResponsiveContainer from 'app/components/ResponsiveContainer';
import Screen from 'app/components/Screen';
import WebVideo from 'app/components/WebVideo';
import { NoticePlacements } from 'app/generated/hygraph';
import useAttraction from 'app/hooks/useAttraction';
import useGuest from 'app/hooks/useGuest';
import useGuestDataRefreshControl from 'app/hooks/useGuestDataRefreshControl';
import useMetaData from 'app/hooks/useMetaData';
import useProduct from 'app/hooks/useProduct';
import usePromptReview from 'app/hooks/usePromptReview';
import useSetScreenOptions from 'app/hooks/useSetScreenOptions';
import { AttractionTicketsStackParamList } from 'app/navigation/AttractionTicketsStackNavigator';
import { getRouteParam } from 'app/navigation/navigationHelper';
import { RootScreenProps } from 'app/navigation/RootNavigator';
import { useI18n } from 'app/providers/I18nProvider';
import AttractionDetailsContactInfo from 'app/screens/AttractionScreen/AttractionContactInfo';
import AttractionFaqs from 'app/screens/AttractionScreen/AttractionFAQs/AttractionFAQs';
import AttractionDetailsHighlights from 'app/screens/AttractionScreen/AttractionHighlights';
import AttractionDetailsHowToVisit from 'app/screens/AttractionScreen/AttractionHowToVisit';
import AttractionMap from 'app/screens/AttractionScreen/AttractionMap';
import AttractionDetailsOffersUpgrades from 'app/screens/AttractionScreen/AttractionOffersUpgrades';
import AttractionDetailsTipsAdvice from 'app/screens/AttractionScreen/AttractionTipsAdvice';
import AttractionDetailsWhatsIncluded from 'app/screens/AttractionScreen/AttractionWhatsIncluded';
import Analytics, { Events } from 'app/services/Analytics';
import { getGalleryImages, getVideo } from 'app/services/AttractionHelper';
import { hasReservationByAttractionKey } from 'app/services/EntitlementHelper';
import { ReservationRequirement } from 'app/services/GuestCenterService.types';
import { getLastRoute } from 'app/utils/navigation';

const MAX_IMAGES = 15;

// export type AttractionScreenProps = AttractionTicketsStackScreenProps<'ProductAttraction'>;
export type AttractionScreenProps = RootScreenProps<AttractionTicketsStackParamList, 'Attraction'>;

export default function AttractionScreen({ route, navigation }: AttractionScreenProps) {
  const productContentKey = getRouteParam(route, 'productId') ?? '';
  const attractionContentKey = getRouteParam(route, 'attractionId') ?? '';

  const { t } = useI18n();
  const promptReview = usePromptReview();

  const { activeEntitlement, hasActiveEntitlementFor } = useGuest();

  const { defaultProductLineup } = useProduct({ productContentKey });
  const attractionContext = useAttraction({
    productContentKey,
    attractionContentKey,
  });
  const { attractionContent, attractionConfig, productContent } = attractionContext;

  const brandPrimaryColor = useToken('colors', 'brand.primary');
  const images: ImageCarouselImage[] = useMemo(
    () => getGalleryImages(attractionContent).slice(0, MAX_IMAGES),
    [attractionContent]
  );
  const video = useMemo(() => getVideo(attractionContent), [attractionContent]);
  const imageRatio = useBreakpointValue([5 / 3, 5 / 3, 5 / 2, 3 / 1]);
  const screenRef = useRef<ScrollView>(null);
  const hasReservation = hasReservationByAttractionKey(
    activeEntitlement,
    attractionContext.attractionConfig?.attractionKey ?? ''
  );

  const { guestDataRefreshControl, guestDataUpdatedAt } = useGuestDataRefreshControl();

  useSetScreenOptions({ title: attractionContent?.name || '' });
  useMetaData('Attraction', { attractionName: attractionContent?.name });

  useFocusEffect(() => {
    if (!navigation?.replace) return;

    const productLineup = hasActiveEntitlementFor(productContentKey)
      ? activeEntitlement?.productLineup
      : defaultProductLineup;
    const inAttractions = productLineup?.attractionGroups?.reduce((groupAcc, group) => {
      return (
        groupAcc ||
        group?.attractions?.some((attraction) => attraction.contentKey === attractionContentKey)
      );
    }, false);

    if (!inAttractions) {
      navigation.replace('Tabs', {
        screen: 'ProductStack',
        params: {
          productId: productContent?.key,
        } as any,
      });
    }
  });

  useFocusEffect(() => {
    const lastRoute = getLastRoute(navigation);
    const attrTickets: keyof AttractionTicketsStackParamList = 'AttractionTickets';

    if (lastRoute?.name === attrTickets) {
      screenRef.current?.scrollTo({ x: 0, y: 0 });
    }

    if (
      promptReview.previouslyPrompted() &&
      activeEntitlement?.reservationRequirement !== ReservationRequirement.Required
    ) {
      promptReview.promptForReview();
    }
  });

  if (!attractionContent || !productContent) return <Loading />;

  return (
    <Screen
      ref={screenRef}
      after={<LinkTicketsPrompt productContentKey={productContentKey} />}
      padded={false}
      refreshControl={guestDataRefreshControl}
      testID="ProductAttractionScreen"
    >
      <Box>
        {images.length > 0 && (
          <ImageCarousel
            _swiper={{
              onIndexChanged: () => {
                Analytics.trackEvent(Events.AttractionImageGalleryControls, {
                  productKey: productContentKey,
                  attractionKey: attractionContentKey,
                });
              },
            }}
            images={images}
            ratio={imageRatio}
          />
        )}
      </Box>

      {!hasReservation && (
        <ResponsiveContainer bgColor="brand.secondary" padded py="6">
          <HTMLCollapsible
            color="white"
            html={attractionContent.intro || ''}
            numberOfLines={3}
            onExpand={() => {
              Analytics.trackEvent(Events.AttractionExpandDescription, {
                productKey: productContentKey,
                attractionKey: attractionContentKey,
              });
            }}
          />
        </ResponsiveContainer>
      )}

      <VStack space="8">
        <ResponsiveContainer padded>
          <Notices
            attractionContentKey={attractionContentKey}
            placement={NoticePlacements.AttractionContentTop}
            productContentKey={productContentKey}
          />

          <IconTitle
            icon={<PhoneQR color={brandPrimaryColor} />}
            padded
            title={t('gen_attraction_content_visit_title')}
          />
          {attractionConfig ? (
            <AttractionDetailsHowToVisit
              attractionConfig={attractionConfig}
              attractionContent={attractionContent}
              productContent={productContent}
            />
          ) : (
            <Spinner />
          )}
        </ResponsiveContainer>

        <Box />

        <ResponsiveContainer padded>
          <VStack space="6">
            <Accordion
              sections={[
                {
                  id: 'map',
                  title: t('gen_attraction_content_map_title'),
                  icon: <MapWithPin color={brandPrimaryColor} />,
                  content: (
                    <AttractionMap
                      attraction={attractionContent}
                      productContentKey={productContentKey}
                    />
                  ),
                  _collapsible: {
                    renderChildrenCollapsed: false,
                  },
                },
                {
                  id: 'contactInfo',
                  title: t('gen_attraction_content_contact_title'),
                  icon: <PhoneHours color={brandPrimaryColor} />,
                  content: <AttractionDetailsContactInfo attraction={attractionContent} />,
                },
                {
                  id: 'whatsIncluded',
                  title: t('gen_attraction_content_included_title'),
                  icon: <TicketIncluded color={brandPrimaryColor} />,
                  content: <AttractionDetailsWhatsIncluded attraction={attractionContent} />,
                },
                ...((attractionContent?.offers?.length ?? 0) > 0
                  ? [
                      {
                        id: 'offersUpgrades',
                        title: t('gen_attraction_content_offers_title'),
                        icon: <TicketOutlinePlus color={brandPrimaryColor} />,
                        content: (
                          <AttractionDetailsOffersUpgrades
                            attraction={attractionContent}
                            productContentKey={productContentKey}
                          />
                        ),
                      },
                    ]
                  : []),
                {
                  id: 'tipsAdvice',
                  title: t('gen_attraction_content_advice_title'),
                  icon: <ChatBubbleCheck color={brandPrimaryColor} />,
                  content: <AttractionDetailsTipsAdvice attraction={attractionContent} />,
                },
                {
                  id: 'highlights',
                  title: t('gen_attraction_content_highlights_title'),
                  icon: <Star color={brandPrimaryColor} />,
                  content: <AttractionDetailsHighlights attraction={attractionContent} />,
                },
                ...((attractionContent?.faqs?.length ?? 0) > 0
                  ? [
                      {
                        id: 'faq',
                        title: t('gen_attraction_content_faqs_title'),
                        icon: <Star color={brandPrimaryColor} />,
                        content: (
                          <AttractionFaqs
                            attraction={attractionContent}
                            productContentKey={productContentKey}
                          />
                        ),
                      },
                    ]
                  : []),
              ]}
              testID="AttractionDetailsAccordion"
              onExpand={({ id }) => {
                Analytics.trackEvent(Events.AttractionExpandAccordion, {
                  productKey: productContentKey,
                  attractionKey: attractionContentKey,
                  contentId: id,
                });
              }}
            />

            {video?.url && (
              <VStack space={1}>
                <WebVideo url={video.url} />
                {video.caption && (
                  <Text color="gray.500" fontSize="xs" lineHeight="xs" textAlign="center">
                    {video.caption}
                  </Text>
                )}
              </VStack>
            )}
          </VStack>
        </ResponsiveContainer>

        <Box>
          {guestDataUpdatedAt}

          <AttractionSupportInfo
            entitlement={activeEntitlement}
            productContentKey={productContent.key}
            productType={productContent.type}
          />
        </Box>
      </VStack>
    </Screen>
  );
}
